import React, { useState, useEffect } from "react";
import { DataGrid, GridToolbar, GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, ChipProps } from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import { SUCCESS_TOAST, ERROR_TOAST } from "../../helpers/toasters";
import { getTilesList, deleteTile, updateTile } from "../../API/tile-mgmt";
import AddTile from "./addTile";
import MainTable from "../../components/Datagrid/dataGridTable";
import "./manageLiveTiles.css";
import { useSelector } from "react-redux";

export default function ManageLiveTiles(props: { search?: string }) {
  const defaultTileState = {
    category: "",
    thumbnailUploaded: false,
    url: "",
    status: "active" as "active" | "inactive",
    action: true,
  };

  const [tiles, setTiles] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState(5);
  const [searchRows, setSearchRows] = useState<any[]>([]);
  const [updateTileModal, setUpdateTileModal] = useState<boolean>(false);
  const [updatingRow, setUpdatingRow] = useState<any>(defaultTileState);
  const user = useSelector((state: any) => state.auth);
  useEffect(() => {
    getFirstData();
  }, []);

  useEffect(() => {
    if (!props.search?.trim()) {
      setSearchRows(tiles);
    } else {
      const filteredRows = tiles.filter((tile: any) =>
        (tile.category ?? "")
          .toLowerCase()
          .includes(props.search?.toLowerCase()) ||
        (tile.url ?? "").toLowerCase().includes(props.search?.toLowerCase())
      );
      setSearchRows(filteredRows.length > 0 ? filteredRows : []);
    }
  }, [props.search, tiles]);

  const getFirstData = async () => {
    try {
      const data = await getTilesList();
      setTiles(data);
      setRows(data);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch tiles:", error);
      ERROR_TOAST("Failed to fetch tiles ❌");
    }
  };

  const handleDelete = async (tile: any) => {
    if (window.confirm("Are you sure you want to delete this tile?")) {
      try {
        await deleteTile(tile._id);
        SUCCESS_TOAST("Tile deleted successfully! ✅");
        getFirstData();
      } catch (error) {
        console.error("Failed to delete tile:", error);
        ERROR_TOAST("Failed to delete tile ❌");
      }
    }
  };

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 30,
    height: 17,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": { width: 15 },
      "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(9px)" },
    },
    "& .MuiSwitch-switchBase": {
      padding: 3,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "#EA1857",
        },
        "& .MuiSwitch-thumb": { backgroundColor: "#fff" },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 10,
      height: 10,
      borderRadius: 6,
      backgroundColor: "#8E8EA1",
      transition: theme.transitions.create(["width"], { duration: 200 }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: "rgba(0,0,0,.10)",
      boxSizing: "border-box",
    },
  }));

  const baseColumns = [
    { field: "_id", hide: true },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      editable: false,
    },
    {
      field: "thumbnailUploaded",
      headerName: "Thumbnail Uploaded",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value ? "Yes" : "No"}</span>
      ),
    },
    {
      field: "url",
      headerName: "URL",
      width: 200,
      editable: false,
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value}</span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <AntSwitch
          checked={params.value}
          onChange={async (e) => {
            const updatedTile = {
              ...params.row,
              action: e.target.checked,
              status: e.target.checked ? "active" : "inactive",
            };
            try {
              await updateTile(updatedTile);
              getFirstData();
            } catch (error) {
              ERROR_TOAST("Failed to update tile status ❌");
            }
          }}
        />
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: (cellValues: GridRenderCellParams) => (
        <EditRoundedIcon
          style={{ color: "#8E8EA1", cursor: "pointer" }}
          onClick={() => {
            setUpdatingRow(cellValues.row);
            setUpdateTileModal(true);
          }}
        />
      ),
    },
  ];

  // Delete column visible only to superusers
  const deleteColumn = [
    {
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (cellValues: GridRenderCellParams) => (
        <DeleteRoundedIcon
          style={{ color: "#8E8EA1", cursor: "pointer" }}
          onClick={() => handleDelete(cellValues.row)}
        />
      ),
    },
  ];

  // Combine columns based on user.isSuper
  const columns = user.isSuper || user.isContentAdmin ? [...baseColumns, ...deleteColumn] : baseColumns;


  const handleClose = () => {
    setUpdateTileModal(false);
    setUpdatingRow(defaultTileState); // Reset updatingRow when closing
    getFirstData();
  };

  const handleAddNewTile = () => {
    setUpdatingRow(defaultTileState); // Reset to default state for new tile
    setUpdateTileModal(true);
  };

  return (
    <div>
      <div className="tableHeader active-courses">
        <div className="tableHeader">
          <button className="addTileBtn" onClick={handleAddNewTile}>
            <div className="btnContainerAT">
              <span>Add New Tile</span>
              <span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M11.25 5.25H6.75V0.75C6.75 0.551088 6.67098 0.360322 6.53033 0.21967C6.38968 0.0790178 6.19891 0 6 0C5.80109 0 5.61032 0.0790178 5.46967 0.21967C5.32902 0.360322 5.25 0.551088 5.25 0.75V5.25H0.75C0.551088 5.25 0.360322 5.32902 0.21967 5.46967C0.0790178 5.61032 0 5.80109 0 6C0 6.19891 0.0790178 6.38968 0.21967 6.53033C0.360322 6.67098 0.551088 6.75 0.75 6.75H5.25V11.25C5.25 11.4489 5.32902 11.6397 5.46967 11.7803C5.61032 11.921 5.80109 12 6 12C6.19891 12 6.38968 11.921 6.53033 11.7803C6.67098 11.6397 6.75 11.4489 6.75 11.25V6.75H11.25C11.4489 6.75 11.6397 6.67098 11.7803 6.53033C11.921 6.38968 12 6.19891 12 6C12 5.80109 11.921 5.61032 11.7803 5.46967C11.6397 5.32902 11.4489 5.25 11.25 5.25Z"
                    fill="#EB1556"
                  />
                </svg>
              </span>
            </div>
          </button>
        </div>
      </div>

      {searchRows.length === 0 && props.search?.trim() ? (
        <p className="no-results">🚫 No results found</p>
      ) : (
        <MainTable
          rows={searchRows.length > 0 ? searchRows : rows}
          columns={columns}
          loading={loading}
        />
      )}

      {updateTileModal && (
        <AddTile handleClose={handleClose} data={updatingRow} />
      )}
    </div>
  );
}